import './companion.css'
import { track } from '../analytics'
import { loadWorldContent } from '../content/loadWorldContent'
import { getThumbUrl, requestPersistence } from '../photoStore'
import { evaluateAll, newlyCompleted } from '../quests'
import {
  hasAnnounced,
  loadQuestState,
  markAnnounced,
  markPhotographed,
  markSeen,
  markUnphotographed,
  saveQuestState,
  type DiscoverableKind,
} from '../questState'
import type { Attraction, Settlement } from '../types'
import { createLocator, type Fix, type LocatorStatus } from './locator'
import {
  arrivalLine,
  greeting,
  photoPrompt,
  questIntro,
  reviewNotice,
  speaker,
} from '../content/narration'
import {
  captureSello,
  deleteSello,
  getSelloThumbUrl,
  hasRenderedPhoto,
  hasSello,
} from './sello'
import {
  canDiscover,
  collapseDuplicates,
  formatDistance,
  loadStandpoints,
  rankByDistance,
  type Ranked,
  type Standpoint,
} from './visitPoints'

const GREETED_KEY = 'roam:arenal:companion:greeted'
/** Recomputing distances on every fix is wasteful; phones deliver these fast. */
const RANK_INTERVAL_MS = 4000

const content = loadWorldContent()
const questState = loadQuestState()
const standpoints = loadStandpoints()

const entriesById = new Map<string, Attraction | Settlement>([
  ...content.attractions.map((a) => [a.id, a] as const),
  ...content.settlements.map((s) => [s.id, s] as const),
])

const el = <T extends HTMLElement>(id: string) => document.getElementById(id) as T

const dom = {
  status: el<HTMLParagraphElement>('status'),
  locate: el<HTMLButtonElement>('btn-locate'),
  arrival: el<HTMLDivElement>('arrival'),
  nearList: el<HTMLUListElement>('near-list'),
  nearEmpty: el<HTMLParagraphElement>('near-empty'),
  questList: el<HTMLUListElement>('quest-list'),
  bookList: el<HTMLUListElement>('book-list'),
  bookEmpty: el<HTMLParagraphElement>('book-empty'),
  sheet: el<HTMLElement>('sheet'),
  sheetTitle: el<HTMLHeadingElement>('sheet-title'),
  sheetKicker: el<HTMLSpanElement>('sheet-kicker'),
  sheetBody: el<HTMLDivElement>('sheet-body'),
  sheetClose: el<HTMLButtonElement>('sheet-close'),
  elder: el<HTMLElement>('elder'),
  elderText: el<HTMLParagraphElement>('elder-text'),
  elderCounter: el<HTMLSpanElement>('elder-counter'),
  elderNext: el<HTMLButtonElement>('elder-next'),
  elderNotice: el<HTMLParagraphElement>('elder-notice'),
  capture: el<HTMLInputElement>('capture'),
  toast: el<HTMLDivElement>('toast'),
  tabs: {
    near: el<HTMLElement>('tab-near'),
    quests: el<HTMLElement>('tab-quests'),
    book: el<HTMLElement>('tab-book'),
  },
  navs: {
    near: el<HTMLButtonElement>('nav-near'),
    quests: el<HTMLButtonElement>('nav-quests'),
    book: el<HTMLButtonElement>('nav-book'),
  },
}

let ranked: Ranked[] = []
let lastRankAt = 0
let openPlaceId: string | null = null
let lastFocused: HTMLElement | null = null

// Toast --------------------------------------------------------------------

let toastTimer: number | undefined
function toast(message: string) {
  dom.toast.textContent = message
  dom.toast.dataset.show = 'true'
  window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => {
    dom.toast.dataset.show = 'false'
  }, 3200)
}

// Dialog plumbing ----------------------------------------------------------

function openDialog(node: HTMLElement) {
  lastFocused = document.activeElement as HTMLElement | null
  node.classList.remove('hidden')
  node.setAttribute('aria-hidden', 'false')
  node.removeAttribute('inert')
}

function closeDialog(node: HTMLElement) {
  node.classList.add('hidden')
  node.setAttribute('aria-hidden', 'true')
  node.setAttribute('inert', '')
  lastFocused?.focus()
}

// Elder --------------------------------------------------------------------

/** Held so a second call (a quest completing mid-greeting) cannot stack handlers. */
let elderAdvance: (() => void) | null = null

function runElder(pages: string[], onDone?: () => void) {
  if (elderAdvance) dom.elderNext.removeEventListener('click', elderAdvance)
  let index = 0
  const render = () => {
    dom.elderText.textContent = pages[index]
    dom.elderCounter.textContent = pages.length > 1 ? `${index + 1} / ${pages.length}` : ''
    dom.elderNext.textContent = index === pages.length - 1 ? 'Right then' : 'Next'
  }
  dom.elderNotice.textContent = reviewNotice()
  render()
  openDialog(dom.elder)
  dom.elderNext.focus()

  const advance = () => {
    index += 1
    if (index < pages.length) {
      render()
      return
    }
    dom.elderNext.removeEventListener('click', advance)
    elderAdvance = null
    closeDialog(dom.elder)
    onDone?.()
  }
  elderAdvance = advance
  dom.elderNext.addEventListener('click', advance)
}

function greetIfFirstVisit() {
  if (localStorage.getItem(GREETED_KEY)) return
  const intro = questIntro(content.quests[0]?.id ?? '')
  const pages = [...greeting().pages, ...(intro ? [intro] : [])]
  runElder(pages, () => {
    try {
      localStorage.setItem(GREETED_KEY, new Date().toISOString())
    } catch {
      // A player who cannot persist this simply gets greeted again.
    }
  })
}

// Location -----------------------------------------------------------------

const STATUS_TEXT: Record<LocatorStatus, string> = {
  idle: 'Location off',
  unsupported: 'This browser cannot share location',
  prompting: 'Asking for location…',
  denied: 'Location blocked — allow it in your browser settings to find places near you',
  unavailable: 'No signal for a location fix yet',
  timeout: 'Still looking for a fix…',
  tracking: 'Locating…',
}

const locator = createLocator({
  onStatus: (status) => {
    dom.status.textContent = STATUS_TEXT[status]
    dom.locate.dataset.on = String(status === 'tracking' || status === 'prompting')
    dom.locate.textContent = status === 'idle' ? 'Find me' : 'Stop'
  },
  onFix: (fix) => {
    dom.status.textContent = `Located to within ${formatDistance(fix.accuracyM)}`
    onPosition(fix)
  },
})

function onPosition(fix: Fix) {
  const now = Date.now()
  if (now - lastRankAt < RANK_INTERVAL_MS) return
  lastRankAt = now
  ranked = rankByDistance(standpoints, fix)
  const arrived = ranked.filter((entry) => canDiscover(entry, fix.accuracyM))
  let discovered = false
  for (const entry of arrived) {
    if (markSeen(questState, entry.point.id, entry.point.kind)) {
      discovered = true
      track('arrived_on_site', { id: entry.point.id, accuracy: Math.round(fix.accuracyM) })
    }
  }
  if (discovered) {
    saveQuestState(questState)
    announceQuests()
  }
  showArrival(arrived[0]?.point)
  renderNear()
  if (discovered) renderQuests()
}

function showArrival(point: Standpoint | undefined) {
  if (!point) {
    dom.arrival.hidden = true
    return
  }
  const line = arrivalLine(point.id)
  if (!line) {
    dom.arrival.hidden = true
    return
  }
  dom.arrival.hidden = false
  dom.arrival.replaceChildren(
    Object.assign(document.createElement('p'), {
      className: 'who',
      textContent: `${speaker().name}, at ${point.name}`,
    }),
    Object.assign(document.createElement('p'), { className: 'said', textContent: line }),
  )
}

// Rendering ----------------------------------------------------------------

function chip(text: string, tone?: string): HTMLSpanElement {
  const node = document.createElement('span')
  node.className = 'chip'
  node.textContent = text
  if (tone) node.dataset.tone = tone
  return node
}

function renderNear() {
  const list = ranked.length
    ? ranked
    : standpoints.map((point) => ({ point, distanceM: Number.NaN, within: false }))
  dom.nearEmpty.hidden = ranked.length > 0
  if (!ranked.length && locator.status() !== 'idle') {
    dom.nearEmpty.textContent = STATUS_TEXT[locator.status()]
  }

  dom.nearList.replaceChildren(
    ...collapseDuplicates(list).map((entry) => {
      const { point, distanceM, within } = entry
      const item = document.createElement('li')
      const card = document.createElement('button')
      card.type = 'button'
      card.className = 'card'
      card.dataset.within = String(within)

      const top = document.createElement('div')
      top.className = 'card-top'
      top.append(
        Object.assign(document.createElement('h3'), {
          className: 'card-name',
          textContent: point.name,
        }),
        Object.assign(document.createElement('span'), {
          className: 'card-distance',
          textContent: Number.isNaN(distanceM) ? '' : formatDistance(distanceM),
        }),
      )

      const chips = document.createElement('div')
      chips.className = 'chips'
      if (within) chips.append(chip('You are here', 'here'))
      if (questState.discoveries[point.id]) chips.append(chip('Found', 'seen'))
      if (point.visit.access !== 'public') chips.append(chip(point.visit.access, 'paid'))
      if (point.visit.hazard) chips.append(chip('Read before you go', 'hazard'))

      card.append(
        top,
        Object.assign(document.createElement('p'), {
          className: 'card-summary',
          textContent: point.summary,
        }),
        chips,
      )
      card.addEventListener('click', () => openPlace(point))
      item.append(card)
      return item
    }),
  )
}

function renderQuests() {
  const progress = evaluateAll(content.quests, questState)
  dom.questList.replaceChildren(
    ...content.quests.map((quest, index) => {
      const state = progress[index]
      const item = document.createElement('li')
      const box = document.createElement('article')
      box.className = 'quest'
      box.dataset.complete = String(state.complete)

      const head = document.createElement('div')
      head.className = 'quest-head'
      head.append(
        Object.assign(document.createElement('h3'), {
          className: 'quest-title',
          textContent: quest.title,
        }),
        Object.assign(document.createElement('span'), {
          className: 'quest-count',
          textContent: `${state.done} / ${state.total}`,
        }),
      )

      const steps = document.createElement('ul')
      steps.className = 'steps'
      steps.append(
        ...state.steps.map((step) => {
          const li = document.createElement('li')
          li.className = 'step'
          li.dataset.done = String(step.done)
          li.append(
            Object.assign(document.createElement('span'), {
              className: 'step-mark',
              textContent: step.done ? '✓' : '·',
            }),
            Object.assign(document.createElement('span'), {
              textContent:
                step.need > 1 ? `${step.text} (${step.have}/${step.need})` : step.text,
            }),
          )
          return li
        }),
      )

      box.append(
        head,
        Object.assign(document.createElement('p'), {
          className: 'quest-premise',
          textContent: questIntro(quest.id) ?? quest.premise,
        }),
        steps,
      )
      item.append(box)
      return item
    }),
  )
}

async function renderBook() {
  const ids = Object.keys(questState.discoveries).filter((id) => entriesById.has(id))
  const pairs = await Promise.all(
    ids.map(async (id) => ({
      id,
      name: entriesById.get(id)?.name ?? id,
      rendered: await getThumbUrl(id),
      sello: await getSelloThumbUrl(id),
    })),
  )
  const filled = pairs.filter((pair) => pair.rendered || pair.sello)
  dom.bookEmpty.hidden = filled.length > 0
  dom.bookList.replaceChildren(
    ...filled.map((pair) => {
      const item = document.createElement('li')
      item.className = 'book-entry'
      const strip = document.createElement('div')
      strip.className = 'pair'
      for (const [url, label] of [
        [pair.rendered, 'No game shot'],
        [pair.sello, 'No sello yet'],
      ] as const) {
        if (url) {
          const img = document.createElement('img')
          img.src = url
          img.alt = ''
          img.loading = 'lazy'
          strip.append(img)
        } else {
          strip.append(
            Object.assign(document.createElement('div'), {
              className: 'missing',
              textContent: label,
            }),
          )
        }
      }
      item.append(
        strip,
        Object.assign(document.createElement('p'), {
          className: 'book-name',
          textContent: pair.name,
        }),
      )
      return item
    }),
  )
}

// Place sheet --------------------------------------------------------------

function para(text: string, className?: string, tone?: string): HTMLParagraphElement {
  const node = document.createElement('p')
  node.textContent = text
  if (className) node.className = className
  if (tone) node.dataset.tone = tone
  return node
}

function heading(text: string): HTMLHeadingElement {
  return Object.assign(document.createElement('h3'), { textContent: text })
}

async function openPlace(point: Standpoint) {
  openPlaceId = point.id
  const entry = entriesById.get(point.id)
  dom.sheetKicker.textContent = point.visit.access === 'public' ? 'Free to visit' : point.visit.access
  dom.sheetTitle.textContent = point.name

  const body: HTMLElement[] = [para(point.summary)]

  const said = arrivalLine(point.id)
  if (said) body.push(para(said, 'said-quote'))

  if (point.visit.hazard) {
    body.push(heading('Before you go'), para(point.visit.hazard, 'note', 'hazard'))
  }

  body.push(heading('Getting there'), para(point.visit.standing, 'note'))

  const attraction = entry && 'tier' in entry ? entry : undefined
  if (attraction?.howItWorks) {
    body.push(heading('Why it is like that'), para(attraction.howItWorks))
  }
  if (attraction?.discover?.length) {
    const ul = document.createElement('ul')
    ul.append(
      ...attraction.discover.map((line) =>
        Object.assign(document.createElement('li'), { textContent: line }),
      ),
    )
    body.push(heading('Look for'), ul)
  }

  const alreadyShot = await hasSello(point.id)
  const actions = document.createElement('div')
  actions.className = 'actions'
  const shoot = document.createElement('button')
  shoot.type = 'button'
  shoot.className = 'btn'
  shoot.textContent = alreadyShot ? 'Replace sello' : 'Take a sello'
  shoot.addEventListener('click', () => dom.capture.click())
  actions.append(shoot)

  if (alreadyShot) {
    const remove = document.createElement('button')
    remove.type = 'button'
    remove.className = 'btn danger'
    remove.textContent = 'Delete sello'
    remove.addEventListener('click', () => void removeSello(point))
    actions.append(remove)
  } else {
    body.push(para(photoPrompt(), 'note'))
  }

  body.push(actions)

  const selloUrl = await getSelloThumbUrl(point.id)
  if (selloUrl) {
    const img = document.createElement('img')
    img.className = 'shot'
    img.src = selloUrl
    img.alt = `Your photograph of ${point.name}`
    body.push(heading('Your sello'), img)
  }

  if (point.visit.reviewer === 'pending-local-review') {
    body.push(
      para(
        'Access and safety notes here were checked from a desk, not walked. Trust the signs and staff in front of you over this app.',
        'note',
        'review',
      ),
    )
  }

  dom.sheetBody.replaceChildren(...body)
  openDialog(dom.sheet)
  dom.sheetClose.focus()
}

async function removeSello(point: Standpoint) {
  await deleteSello(point.id)
  if (!(await hasRenderedPhoto(point.id))) {
    markUnphotographed(questState, point.id)
    saveQuestState(questState)
    renderQuests()
  }
  track('sello_deleted', { id: point.id })
  toast('Sello deleted')
  void openPlace(point)
  void renderBook()
}

let persistenceAsked = false

async function onCapture(file: File) {
  const id = openPlaceId
  const point = standpoints.find((entry) => entry.id === id)
  if (!id || !point) return
  toast('Saving your sello…')
  const kind: DiscoverableKind = point.kind
  const outcome = await captureSello(file, {
    id,
    placeId: 'arenal',
    subject: point.name,
    caption: point.summary,
  })
  if (outcome === 'unreadable') {
    toast('That image could not be read')
    return
  }
  if (outcome === 'quota') {
    toast('Storage full — sello not saved')
    return
  }
  if (outcome === 'failed') {
    toast('Could not save that sello')
    return
  }

  markSeen(questState, id, kind)
  markPhotographed(questState, id, kind)
  saveQuestState(questState)
  track('sello_captured', { id })
  announceQuests()
  renderQuests()
  renderNear()
  void renderBook()
  void openPlace(point)
  toast('Sello saved')

  if (!persistenceAsked) {
    persistenceAsked = true
    void requestPersistence()
  }
}

// Quest completion ---------------------------------------------------------

function announceQuests() {
  for (const quest of newlyCompleted(content.quests, questState)) {
    if (hasAnnounced(questState, quest.id)) continue
    markAnnounced(questState, quest.id)
    saveQuestState(questState)
    track('quest_completed', { questId: quest.id })
    runElder([`${quest.title} — done.`, quest.reward.text])
  }
}

// Tabs ---------------------------------------------------------------------

type TabName = 'near' | 'quests' | 'book'

function showTab(name: TabName) {
  for (const key of ['near', 'quests', 'book'] as TabName[]) {
    dom.tabs[key].hidden = key !== name
    dom.navs[key].classList.toggle('active', key === name)
    dom.navs[key].setAttribute('aria-selected', String(key === name))
  }
  if (name === 'quests') renderQuests()
  if (name === 'book') void renderBook()
}

// Wiring -------------------------------------------------------------------

dom.locate.addEventListener('click', () => {
  if (locator.status() === 'idle') {
    locator.start()
    track('companion_located', {})
  } else {
    locator.stop()
  }
})

dom.sheetClose.addEventListener('click', () => {
  openPlaceId = null
  closeDialog(dom.sheet)
})

dom.sheet.addEventListener('click', (event) => {
  if (event.target === dom.sheet) {
    openPlaceId = null
    closeDialog(dom.sheet)
  }
})

dom.capture.addEventListener('change', () => {
  const file = dom.capture.files?.[0]
  dom.capture.value = ''
  if (file) void onCapture(file)
})

dom.navs.near.addEventListener('click', () => showTab('near'))
dom.navs.quests.addEventListener('click', () => showTab('quests'))
dom.navs.book.addEventListener('click', () => showTab('book'))

window.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return
  if (!dom.sheet.classList.contains('hidden')) {
    openPlaceId = null
    closeDialog(dom.sheet)
  }
})

/** High-accuracy GPS is expensive; stop it whenever the app is backgrounded. */
document.addEventListener('visibilitychange', () => {
  if (document.hidden) locator.stop()
})

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/companion-sw.js').catch(() => {
      // Offline support is a bonus; the app works without it.
    })
  })
}

renderNear()
renderQuests()
showTab('near')
greetIfFirstVisit()
track('companion_opened', { standpoints: standpoints.length })
